// Καρτέλα «Προκαταβολές» του «Ταμείο» (μόνο με ανοιχτό τον διακόπτη counter_deposits_enabled):
// οι προκαταβολές ταμείου του καταστήματος — ανοιχτές πρώτα, αναζήτηση με τηλέφωνο — με «Νέα
// προκαταβολή» για πελάτη (χωρίς κράτηση) και «Επιστροφή» ανά γραμμή.

import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, Plus, RotateCcw, Search } from 'lucide-react';
import { Btn, Note, Segmented } from '../dineIn/floorEditor/ui';
import { athensDateTime } from '../register/registerFormat';
import { depositsApi } from './api';
import DepositModal from './DepositModal';
import { digits, isOpen, money, sortDeposits, toCents } from './model';
import RefundModal from './RefundModal';
import { ReceiptLine, useDT } from './ui';
import type { DepositView, DepositsConfig } from './types';

type Filter = 'open' | 'all';

export default function DepositsPanel({ storeId, config, canCollect, refreshKey }: {
  storeId: number; config: DepositsConfig; canCollect: boolean; refreshKey?: unknown;
}) {
  const dt = useDT();
  const [filter, setFilter] = useState<Filter>('open');
  const [q, setQ] = useState('');
  const [phone, setPhone] = useState('');
  const [list, setList] = useState<DepositView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ kind: 'new' } | { kind: 'refund'; d: DepositView } | null>(null);

  // Αναζήτηση από 3 ψηφία, με μικρή καθυστέρηση (όχι αίτημα σε κάθε πλήκτρο).
  useEffect(() => {
    const d = digits(q);
    const id = window.setTimeout(() => setPhone(d.length >= 3 ? d : ''), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  const load = useCallback(async () => {
    try {
      const all = await depositsApi.list(storeId, { status: filter, phone: phone || undefined, limit: 100 });
      setList(sortDeposits(all));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [storeId, filter, phone]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const changed = () => { load(); };

  return (
    <div data-testid="deposits-panel" className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[220px]">
          <Segmented<Filter> value={filter} onChange={setFilter}
            options={[{ value: 'open', label: dt('deposits:panel.open') }, { value: 'all', label: dt('deposits:panel.all') }]} />
        </div>
        <label className="relative flex min-w-[200px] max-w-[320px] flex-1 items-center">
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-t3" />
          <input value={q} onChange={e => setQ(e.target.value)} inputMode="tel" placeholder={dt('deposits:panel.search')} aria-label={dt('deposits:panel.search')}
            className="h-11 w-full rounded-lg border border-line bg-surface-2 pl-9 pr-3 text-[14px] text-t1 placeholder:text-t4 focus:border-blue-500 focus:outline-none" />
        </label>
        {canCollect && (
          <Btn variant="primary" className="ml-auto h-11" icon={<Plus className="h-4 w-4" />} onClick={() => setModal({ kind: 'new' })}
            data-testid="deposits-new">
            {dt('deposits:new')}
          </Btn>
        )}
      </div>
      {!config.ready && <Note tone="warn">{dt('deposits:notReady')}</Note>}
      {error ? (
        <div className="grid justify-items-start gap-2">
          <Note tone="error">{dt('deposits:panel.loadFailed', { error })}</Note>
          <Btn onClick={load}>{dt('deposits:panel.retry')}</Btn>
        </div>
      ) : list == null ? (
        <div className="grid gap-2">{[0, 1, 2].map(i => <div key={i} className="h-16 animate-pulse rounded-xl bg-surface-2" />)}</div>
      ) : list.length === 0 ? (
        <div className="grid place-items-center gap-2 py-10 text-center">
          <Banknote className="h-9 w-9 text-t4" />
          <p className="text-t2">{phone ? dt('deposits:panel.emptySearch') : filter === 'open' ? dt('deposits:panel.emptyOpen') : dt('deposits:panel.empty')}</p>
        </div>
      ) : (
        <ul className="grid gap-2" data-testid="deposits-list">
          {list.map(d => (
            <li key={d.id}>
              <DepositRow d={d} canRefund={canCollect} onRefund={() => setModal({ kind: 'refund', d })} />
            </li>
          ))}
        </ul>
      )}

      {modal?.kind === 'new' && (
        <DepositModal storeId={storeId} config={config} owner={{ kind: 'customer', phone: digits(q).length >= 10 ? q : '' }}
          onClose={() => setModal(null)} onDone={changed} />
      )}
      {modal?.kind === 'refund' && (
        <RefundModal storeId={storeId} deposit={modal.d} onClose={() => setModal(null)} onDone={changed} />
      )}
    </div>
  );
}

function DepositRow({ d, canRefund, onRefund }: { d: DepositView; canRefund: boolean; onRefund: () => void }) {
  const dt = useDT();
  const kept = toCents(d.kept);
  const open = isOpen(d);
  const who = d.customer_name || d.customer_phone || dt('deposits:panel.noName');
  const res = d.reservation;
  return (
    <div data-testid={`deposit-row-${d.id}`}
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl border px-3.5 py-2.5 ${d.refund_due && open ? 'border-red-500/60 bg-red-500/5' : 'border-line bg-surface-1'} ${!open && kept === 0 ? 'opacity-70' : ''}`}>
      <div className="grid min-w-0 gap-0.5">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="truncate text-[15px] font-semibold text-t1">{who}</span>
          {d.customer_name && d.customer_phone && <span className="text-[12.5px] text-t3">{d.customer_phone}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-t3">
          <span>{athensDateTime(d.paid_at || d.created_at)}</span>
          <span>·</span>
          <span>{res
            ? dt('deposits:panel.reservation', { date: res.date.split('-').reverse().slice(0, 2).join('/'), time: String(res.time || '').slice(0, 5), people: dt('deposits:people', { count: res.party_size }) })
            : dt('deposits:panel.credit')}</span>
          <span>·</span>
          <ReceiptLine doc="8.4" receipt={d.receipt} pending={d.receipt_pending} error={d.receipt_error} cash={d.method === 'cash'} compact />
        </div>
        {toCents(d.refunded) > 0 && <span className="text-[12.5px] text-t2">{dt('deposits:detail.refunded', { amount: money(d.refunded) })}</span>}
        {toCents(d.used) > 0 && (
          <span data-testid={`deposit-used-${d.id}`} className="text-[12.5px] text-t2">{dt('deposits:detail.used', { amount: money(d.used) })}</span>
        )}
        {d.refund_due && open && (
          <span data-testid="deposit-refund-due" className="text-[12.5px] font-semibold text-red-700 dark:text-red-300">{dt('deposits:detail.refundDue', { amount: money(d.available) })}</span>
        )}
        {kept > 0 && (
          <span className={`text-[12.5px] font-semibold ${d.settlement === 'settled' ? 'text-t2' : 'text-amber-700 dark:text-amber-300'}`} title={d.settlement_error || undefined}>
            {dt('deposits:chip.kept', { amount: money(d.kept) })} · {d.settlement === 'settled' ? dt('deposits:chip.settled') : dt('deposits:chip.settlementPending')}
            {d.settlement !== 'settled' && d.settlement_error && <span className="block font-normal text-t3">{d.settlement_error}</span>}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="grid justify-items-end">
          <span className="text-[16px] font-bold tabular-nums text-t1">{money(open ? d.available : d.amount)}</span>
          <span className="text-[12px] text-t3">{dt(`deposits:method.${d.method}`)} · {dt(`deposits:status.${d.status}`)}</span>
        </div>
        {open && canRefund && (
          <Btn size="sm" variant={d.refund_due ? 'danger' : 'secondary'} icon={<RotateCcw className="h-4 w-4" />} onClick={onRefund}
            data-testid={`deposit-refund-${d.id}`}>
            {dt('deposits:refund')}
          </Btn>
        )}
      </div>
    </div>
  );
}
